import store from 'config/store'
import UseRemoveActiveCampaign from 'hooks/useRemoveActiveCampaign'
import UseStateSnapShot from 'hooks/useStateSnapShot'
import UseStoreSync from 'hooks/UseStoreSync'
import WidgetControllerLayout from 'layouts/Controller'
import { getWidgetReadyPayload } from 'modules/api'
import { handleApiEvent } from 'modules/api'
import { init } from 'modules/bootstrap'
import { receiveExternalEmail, receiveExternalId, receiveExternalJwt } from 'modules/session/actions'
import { setShouldForceShowWidget, setShouldSkipWidgetTargeting } from 'modules/targeting/actions'
import qs from 'query-string'
import React, { useEffect, useState }  from 'react'
import { Provider } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { PUBLIC_EVENTS } from 'shared/constants/api'
import { error,uuid } from 'utils'
import { createEvent } from 'utils/events'
import { listen } from 'utils/message'
import { counterMetric, timingMetric } from 'utils/metrics'
import { setFontStyleSheet, setGlobalUserStyleSheet } from 'utils/styles'
import { fireByDriftlink } from 'utils/targeting'
import { stopTraceMark } from 'utils/trace'
import { parseQueryStringBooleans } from 'utils/url'

import './style.css'

store.name = 'controller'

interface HandlerParams {
  data: Record<string, any>
}

const WidgetControllerPage = ({ location }: { location?: string }) => {
  const [initReady, setInitReady] = useState(false)
  const params = parseQueryStringBooleans(qs.parse(location.search))
  const instanceId = uuid()

  useEffect(() => {
    if(initReady) {
      const loadedDuration = stopTraceMark('widget_core:widget_loaded')
      if (!!loadedDuration) {
        timingMetric('loaded', loadedDuration)
        counterMetric('widget.v2.load', false)
      }

      const { embed = {} } = store.getState()
      setFontStyleSheet(embed)
      setGlobalUserStyleSheet(embed)

      return
    }

    UseStoreSync({
      name: 'controller',
      syncWith: 'chat',
      store
    })

    UseStateSnapShot()
    UseRemoveActiveCampaign()

    listen({
      topic: 'trigger-api-event',
      handler: handleApiEvent
    })

    listen({
      topic: 'log-conductor-error',
      handler: ({ data = {} }: HandlerParams) => error(new Error(`WIDGET_CONDUCTOR: ${data.message}`))
    })

    listen({
      topic: 'get-ready-payload',
      handler: getWidgetReadyPayload
    })

    listen({
      topic: 'identity-provided-email',
      handler: ({ data = {} }: HandlerParams) => {
        store.dispatch(receiveExternalEmail(data.email))
      }
    })

    listen({
      topic: 'hide-widget-on-ready',
      handler: ({ data = {} }) => {
        store.dispatch(setShouldSkipWidgetTargeting(true))
      }
    })

    listen({
      topic: 'show-widget-on-ready',
      handler: ({ data = {} }) => {
        store.dispatch(setShouldForceShowWidget(true))
      }
    })

    listen({
      topic: 'identity-provided-id',
      handler: ({ data = {} }: HandlerParams) => {
        store.dispatch(receiveExternalId(data.externalId))
      }
    })

    listen({
      topic: 'identity-provided-jwt',
      handler: ({ data = {} }: HandlerParams) => {
        store.dispatch(receiveExternalJwt(data.userJwt))
      }
    })

    listen({
      topic: 'host-hash-change',
      handler: ({ data }: HandlerParams) => fireByDriftlink(data.hash)
    })

    listen({
      topic: `init-ready`,
      handler: async () => {
        try {
          !initReady && await init({
            embedId: params.embedId,
            params: {
              skipCampaigns: params.skipCampaigns,
              forceShow: params.forceShow,
              session: {
                sessionId: params.sessionId,
                sessionStarted: params.sessionStarted,
                campaignRefreshToken: params.campaignRefreshToken,
                instanceId
              }
            },
            timing: {
              pageLoadStartTime: params.pageLoadStartTime
            }
          })
          setInitReady(true)
        } catch(err) {
          console.warn(err)
        }
      }
    })


    // make sure we dont trigger api ready until our listener has
    // been setup.
    createEvent(PUBLIC_EVENTS.API_READY)
  }, [params.embedId, params.forceShow, params.skipCampaigns, initReady, params.sessionId, params.sessionStarted, params.campaignRefreshToken, instanceId, params.pageLoadStartTime])

  return (
    <Provider store={store}>
      {initReady && <WidgetControllerLayout />}
    </Provider>
  )
}

export default withRouter(WidgetControllerPage)
